import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './EmbeddedChat.css';

function EmbeddedChat({ userId, userName, onClose }) {
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    if (userId) {
      initializeChat();
    }
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const initializeChat = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      
      // Get or create conversation
      const convResponse = await axios.get(`/api/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const convId = convResponse.data.conversationId;
      setConversationId(convId);
      
      // Load messages
      await loadMessages(convId);
      
      // Mark messages as read
      await markAsRead(convId);
      
      // Start polling for new messages
      pollInterval.current = setInterval(() => {
        loadMessages(convId, true);
      }, 3000);
      
    } catch (error) {
      console.error('Error initializing chat:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (convId, silent = false) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`/api/messages/${convId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(response.data.messages);
      
      if (!silent) {
        await markAsRead(convId);
      }
    } catch (error) {
      if (!silent) {
        console.error('Error loading messages:', error);
      }
    }
  };

  const markAsRead = async (convId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/messages/${convId}/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !conversationId) return;
    
    setSending(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`/api/messages/${conversationId}`, {
        text: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages([...messages, response.data.message]);
      setNewMessage('');
      scrollToBottom();
      
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  if (!userId) {
    return (
      <div className="embedded-chat-empty">
        <div className="empty-icon">💬</div>
        <h3>Select a connection to chat</h3>
        <p>Click the Message button on any connection to start chatting</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="embedded-chat-container">
        <div className="embedded-chat-loading">Loading chat...</div>
      </div>
    );
  }

  return (
    <div className="embedded-chat-container">
      <div className="embedded-chat-header">
        <div className="chat-user-info">
          <div className="user-avatar-small">👤</div>
          <h4>{userName}</h4>
        </div>
        <button onClick={onClose} className="close-chat-btn">✕</button>
      </div>

      <div className="embedded-messages-container">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <p>👋 Start the conversation!</p>
            <p className="hint">Send a message to {userName}</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div key={msg.id} className={`message ${msg.isMine ? 'mine' : 'theirs'}`}>
              <div className="message-bubble">
                <p>{msg.text}</p>
                <span className="message-time">
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {msg.isMine && (msg.isRead ? ' ✓✓' : ' ✓')}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSend} className="embedded-message-input">
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          disabled={sending}
          maxLength={5000}
        />
        <button 
          type="submit" 
          disabled={!newMessage.trim() || sending}
        >
          {sending ? '...' : '📤'}
        </button>
      </form>
    </div>
  );
}

export default EmbeddedChat;
